// Types for MusicKit
interface MusicKitConfig {
  developerToken: string;
  app: {
    name: string;
    build: string;
  };
  storefrontId?: string;
  suppressErrorDialog?: boolean;
}

// Apple Music developer token from environment variables
const DEVELOPER_TOKEN = import.meta.env.VITE_APPLE_MUSIC_DEVELOPER_TOKEN;
// Load MusicKit JS from Apple's CDN
const MUSICKIT_JS_URL = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';

// Import lyricsService for lyrics
import { lyricsService } from './lyricsService';

// Cache for lyrics to avoid repeated API calls
const lyricsCache = new Map();

// Track the currently playing track to avoid unnecessary refreshes
let currentTrackId: string | null = null;
let lastTrackCheckTime = 0;
const TRACK_CHECK_INTERVAL = 5000; // Only check for new tracks every 5 seconds

// Track MusicKit loading state
let musicKitLoadingPromise: Promise<void> | null = null;

export const appleMusicService = {
  loadMusicKitJS: async (): Promise<void> => {
    if (musicKitLoadingPromise) {
      return musicKitLoadingPromise;
    }

    musicKitLoadingPromise = new Promise((resolve, reject) => {
      if (window.MusicKit) {
        console.log('MusicKit already loaded');
        resolve();
        return;
      }

      try {
        console.log('Loading MusicKit.js from:', MUSICKIT_JS_URL);
        const script = document.createElement('script');
        script.src = MUSICKIT_JS_URL;
        script.async = true;
        script.crossOrigin = 'anonymous';
        
        script.onload = () => {
          // Wait for MusicKit to be fully initialized
          const checkMusicKit = () => {
            if (window.MusicKit && typeof window.MusicKit.configure === 'function') {
              console.log('MusicKit JS loaded successfully');
              resolve();
            } else {
              setTimeout(checkMusicKit, 100);
            }
          };
          checkMusicKit();
        };
        
        script.onerror = (error) => {
          console.error('Failed to load MusicKit JS:', error);
          reject(new Error('Failed to load MusicKit JS'));
        };
        
        document.body.appendChild(script);
      } catch (error) {
        console.error('Error loading MusicKit JS:', error);
        reject(error);
      }
    });

    return musicKitLoadingPromise;
  },

  initializeMusicKit: async () => {
    if (!DEVELOPER_TOKEN) {
      console.error('Apple Music developer token not provided');
      throw new Error('Developer token is required to initialize Apple Music');
    }

    try {
      // Ensure MusicKit is loaded
      await appleMusicService.loadMusicKitJS();

      // Configure MusicKit
      const configuration: MusicKitConfig = {
        developerToken: DEVELOPER_TOKEN,
        app: {
          name: 'SyncdIt',
          build: '1.0.0',
        },
        storefrontId: 'us',
        suppressErrorDialog: false
      };

      // Initialize MusicKit instance
      console.log('Calling MusicKit.configure...');
      
      if (!window.MusicKit || typeof window.MusicKit.configure !== 'function') {
        console.error('MusicKit is not available or not properly initialized');
        throw new Error('MusicKit is not available');
      }
      
      const instance = window.MusicKit.configure(configuration);
      console.log('MusicKit initialized successfully');
      
      // Add some debug information
      console.log('MusicKit version:', window.MusicKit.version);
      console.log('Is authorized:', instance.isAuthorized);
      
      // Check for stored authorization state
      if (localStorage.getItem('apple_music_authorized') === 'true' && !instance.isAuthorized) {
        console.log('Found stored authorization but user is not authorized. Attempting to reauthorize...');
        try {
          await instance.authorize();
        } catch (error) {
          console.error('Failed to reauthorize with stored credentials:', error);
          localStorage.removeItem('apple_music_authorized');
        }
      }
      
      return instance;
    } catch (error) {
      console.error('Error initializing MusicKit:', error);
      throw error;
    }
  },

  ensureAuthorization: async (musicKitInstance: any): Promise<boolean> => {
    if (!musicKitInstance) {
      console.error('MusicKit instance not available');
      return false;
    }
    
    try {
      if (!musicKitInstance.isAuthorized) {
        console.log('Apple Music not authorized, requesting authorization...');
        await musicKitInstance.authorize();
        
        // Update local storage after successful authorization
        if (musicKitInstance.isAuthorized) {
          localStorage.setItem('apple_music_authorized', 'true');
          return true;
        } else {
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('Error authorizing Apple Music:', error);
      return false;
    }
  },

  // Get currently playing track through the API
  getCurrentTrack: async (musicKitInstance: any, forceRefresh: boolean = false) => {
    try {
      if (!musicKitInstance) {
        throw new Error('MusicKit instance not available');
      }

      // Ensure user is authorized
      const isAuthorized = await appleMusicService.ensureAuthorization(musicKitInstance);
      if (!isAuthorized) {
        console.error('User is not authorized with Apple Music');
        return null;
      }

      // Check if we should fetch a new track or use cached track data
      // Skip this check if forceRefresh is true
      const now = Date.now();
      if (!forceRefresh && currentTrackId && now - lastTrackCheckTime < TRACK_CHECK_INTERVAL) {
        console.log('Using cached track data, last check was recent');
        return null; // Return null to indicate no change
      }
      
      // Update the last check time
      lastTrackCheckTime = now;

      // Get the recently played tracks
      try {
        const recentPlayed = await musicKitInstance.api.music(
          'v1/me/recent/played/tracks?types=songs,music-videos&limit=1'
        );
        
        if (!recentPlayed?.data?.data || recentPlayed.data.data.length === 0) {
          console.log('No recently played tracks');
          
          // If we had a track before but now we don't, clear the current track
          if (currentTrackId) {
            currentTrackId = null;
            return null; // Return null to indicate a change (stopped playing)
          }
          
          return null;
        }
        
        const mostRecentTrack = recentPlayed.data.data[0];
        const newTrackId = mostRecentTrack.id;
        const attr = mostRecentTrack.attributes;
        
        // Check if the track has changed
        if (newTrackId === currentTrackId) {
          console.log('Track has not changed:', attr.name);
          return null; // Return null to indicate no change
        }
        
        // Track has changed, update currentTrackId
        console.log('Track has changed to:', attr.name);
        currentTrackId = newTrackId;
        
        return {
          item: {
            id: newTrackId,
            name: attr.name,
            artists: [{ name: attr.artistName }],
            album: {
              name: attr.albumName,
              images: [{ url: attr.artwork?.url?.replace('{w}', '300').replace('{h}', '300') || '' }],
            },
            duration_ms: attr.durationInMillis || 0,
          },
          // Since we can't know if it's actually playing, we'll assume it is
          is_playing: true,
          progress_ms: 0, // We don't know the progress, so start from beginning
          device: {
            name: 'Apple Music',
            type: 'Apple Music',
          },
          trackChanged: true, // Flag to indicate that the track has changed
        };
      } catch (error) {
        console.error('Error fetching recent tracks from Apple Music:', error);
        return null;
      }
    } catch (error) {
      console.error('Error in getCurrentTrack:', error);
      return null;
    }
  },

  getQueue: async (musicKitInstance: any) => {
    try {
      if (!musicKitInstance) {
        throw new Error('MusicKit instance not available');
      }

      // Ensure user is authorized
      const isAuthorized = await appleMusicService.ensureAuthorization(musicKitInstance);
      if (!isAuthorized) {
        console.error('User is not authorized with Apple Music');
        return { queue: [] };
      }

      // Get recently played as a proxy for queue
      try {
        const recentPlayed = await musicKitInstance.api.music(
          'v1/me/recent/played/tracks?types=songs,music-videos&limit=10'
        );
        
        if (!recentPlayed?.data?.data || recentPlayed.data.data.length === 0) {
          return { queue: [] };
        }
        
        // Format the recently played tracks to match the expected queue format
        const formattedQueue = recentPlayed.data.data.map((track: any) => ({
          id: track.id,
          name: track.attributes.name,
          artists: [{ name: track.attributes.artistName }],
          album: {
            name: track.attributes.albumName,
            images: [{ url: track.attributes.artwork?.url?.replace('{w}', '300').replace('{h}', '300') || '' }],
          },
          duration_ms: track.attributes.durationInMillis || 0,
        }));

        return { queue: formattedQueue };
      } catch (error) {
        console.error('Error fetching recent tracks for queue:', error);
        return { queue: [] };
      }
    } catch (error) {
      console.error('Error in getQueue:', error);
      return { queue: [] };
    }
  },

  getLyrics: async (musicKitInstance: any, trackId: string) => {
    try {
      if (!musicKitInstance) {
        throw new Error('MusicKit instance not available');
      }

      // Ensure user is authorized
      const isAuthorized = await appleMusicService.ensureAuthorization(musicKitInstance);
      if (!isAuthorized) {
        console.error('User is not authorized with Apple Music');
        return {
          lyrics: 'User not authorized with Apple Music',
          source: 'error',
          synced: false,
        };
      }

      // Get track details from the API
      try {
        // Get the track from the catalog
        const trackResponse = await musicKitInstance.api.music(`v1/catalog/us/songs/${trackId}`);
        if (!trackResponse?.data?.data || trackResponse.data.data.length === 0) {
          return {
            lyrics: 'Track information not found',
            source: 'error',
            synced: false,
          };
        }
        
        const track = trackResponse.data.data[0];
        const trackName = track.attributes.name;
        const artistName = track.attributes.artistName;
        const albumName = track.attributes.albumName;
        const durationInSeconds = track.attributes.durationInMillis / 1000; // Convert to seconds for SyncdIt-Lyrics
        
        // Check cache first
        const cacheKey = `${trackName}-${artistName}`;
        if (lyricsCache.has(cacheKey)) {
          console.log('Returning cached lyrics for:', cacheKey);
          return lyricsCache.get(cacheKey);
        }
        
        // Use lyricsService to get lyrics
        console.log(`Searching for lyrics with SyncdIt-Lyrics: ${trackName} ${artistName}`);
        const syncditLyricsResult = await lyricsService.getLyrics(
          artistName, 
          trackName, 
          albumName, 
          durationInSeconds
        );
        
        if (syncditLyricsResult) {
          const result = {
            lyrics: syncditLyricsResult.lyrics,
            source: 'syncdit-lyrics',
            synced: syncditLyricsResult.synced,
            syncedLyrics: syncditLyricsResult.syncedLyrics
          };
          
          // Cache the result
          lyricsCache.set(cacheKey, result);
          return result;
        }
        
        // Fallback if lrcLib fails
        const fallbackResult = {
          lyrics: `Searching for lyrics to "${trackName}" by ${artistName}...\n\nNo lyrics found. Try playing another song.`,
          source: 'fallback',
          synced: false
        };
        
        // Cache the fallback result
        lyricsCache.set(cacheKey, fallbackResult);
        return fallbackResult;
      } catch (error) {
        console.error('Error fetching track details for lyrics:', error);
        return {
          lyrics: 'Error fetching track details. Please try again later.',
          source: 'error',
          synced: false,
        };
      }
    } catch (error) {
      console.error('Error in getLyrics:', error);
      return {
        lyrics: 'Error fetching lyrics. Please try again later.',
        source: 'error',
        synced: false,
      };
    }
  },

  // Get user's recently played tracks
  getRecentlyPlayed: async (musicKitInstance: any, limit = 10) => {
    try {
      if (!musicKitInstance) {
        throw new Error('MusicKit instance not available');
      }

      // Ensure user is authorized
      const isAuthorized = await appleMusicService.ensureAuthorization(musicKitInstance);
      if (!isAuthorized) {
        console.error('User is not authorized with Apple Music');
        return [];
      }

      const response = await musicKitInstance.api.music(
        `v1/me/recent/played/tracks?types=songs,music-videos&limit=${limit}`
      );
      
      if (!response?.data?.data) {
        return [];
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching recently played tracks:', error);
      return [];
    }
  },

  // Get user's heavy rotation content
  getHeavyRotation: async (musicKitInstance: any, limit = 10) => {
    try {
      if (!musicKitInstance) {
        throw new Error('MusicKit instance not available');
      }

      // Ensure user is authorized
      const isAuthorized = await appleMusicService.ensureAuthorization(musicKitInstance);
      if (!isAuthorized) {
        console.error('User is not authorized with Apple Music');
        return [];
      }

      const response = await musicKitInstance.api.music(
        `v1/me/history/heavy-rotation?types=albums,playlists,songs&limit=${limit}`
      );
      
      if (!response?.data?.data) {
        return [];
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching heavy rotation content:', error);
      return [];
    }
  },

  // Add method to reset the current track
  resetCurrentTrack: () => {
    currentTrackId = null;
    lastTrackCheckTime = 0;
    console.log('Reset current Apple Music track');
  }
}; 